import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Stack, Tooltip, Typography } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { Icon } from "@iconify/react";
import type { UseUploadResult } from "./useUpload";
import type { UploadFilter, UploadItem } from "./types";
import { dropZone as dropZoneCopy, emptyFilter } from "./copy";
import StatusStrip from "./components/StatusStrip";
import SessionFailedBanner from "./components/SessionFailedBanner";
import UploadItemRow from "./components/UploadItemRow";
import type { FileUpload } from "../components/inputs/types";
import type { CaseFile } from "../pages/cases/types";
import { formatSize } from "../utils";
import { openFileInNewTab } from "../pages/cases/utils";

/** Mirrors werkzeug's secure_filename: non-alphanumeric/dot/dash → underscore, lowercase. */
function normalizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").toLowerCase();
}

interface UploadResultsPanelProps {
  controller: UseUploadResult;
  pendingFiles?: (FileUpload | CaseFile)[];
  onRemoveFile?: (filename: string) => void;
  onResubmit?: () => void;
  getPreviewUrl?: (f: CaseFile) => Promise<string | null | undefined>;
}

function filterItems(items: UploadItem[], filter: UploadFilter): UploadItem[] {
  switch (filter) {
    case "ERRORS":
      return items.filter((i) => i.status === "REJECTED");
    case "PENDING":
      return items.filter(
        (i) => i.status === "REQUESTED" || i.status === "UPLOADED" || i.status === "PROCESSING"
      );
    case "ACCEPTED":
      return items.filter((i) => i.status === "ACCEPTED");
    default:
      return items;
  }
}

const RemoveButton: React.FC<{ onRemove: () => void }> = ({ onRemove }) => (
  <Box
    component="button"
    onClick={onRemove}
    aria-label="Remove file"
    sx={{
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      background: "transparent",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "rgba(0,0,0,0.28)",
      flexShrink: 0,
      transition: "color 150ms, background 150ms",
      "&:hover": {
        color: "#D32F2F",
        background: "rgba(211,47,47,0.08)",
      },
      "&:focus-visible": {
        outline: "2px solid #266A4A",
        outlineOffset: "2px",
      },
    }}
  >
    <Icon icon="mdi:close" width={16} />
  </Box>
);

const PreviewButton: React.FC<{ onPreview: () => void }> = ({ onPreview }) => (
  <Box
    component="button"
    onClick={onPreview}
    aria-label="Preview file"
    sx={{
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      background: "transparent",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "rgba(0,0,0,0.28)",
      flexShrink: 0,
      transition: "color 150ms, background 150ms",
      "&:hover": {
        color: "#266A4A",
        background: "rgba(38,106,74,0.08)",
      },
      "&:focus-visible": {
        outline: "2px solid #266A4A",
        outlineOffset: "2px",
      },
    }}
  >
    <Icon icon="mdi:open-in-new" width={16} />
  </Box>
);

const OverflowTooltip: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  const check = () => {
    const el = ref.current?.firstChild as HTMLElement | null;
    if (el) setOverflow(el.scrollWidth > el.clientWidth);
  };
  return (
    <Tooltip title={overflow ? title : ""} placement="top">
      <Box ref={ref} onMouseEnter={check} sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        {children}
      </Box>
    </Tooltip>
  );
};

const ExistingFileRow: React.FC<{ name: string; onPreview?: () => void; onRemove?: () => void }> = ({ name, onPreview, onRemove }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2,
      py: 1.25,
      borderRadius: "12px",
      background: "#F6FBF4",
      border: "1px solid rgba(0,0,0,0.05)",
      width: "100%",
      minWidth: 0,
    }}
  >
    <Icon icon="mdi:file-check-outline" color="#266A4A" width={18} style={{ flexShrink: 0 }} />
    <OverflowTooltip title={name}>
      <Typography
        sx={{
          fontSize: 13,
          fontFamily: "Poppins, sans-serif",
          color: "#0B1F15",
          fontWeight: 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Typography>
    </OverflowTooltip>
    {onPreview && <PreviewButton onPreview={onPreview} />}
    {onRemove && <RemoveButton onRemove={onRemove} />}
  </Box>
);

const PendingRow: React.FC<{ name: string; size?: number; onRemove?: () => void }> = ({ name, size, onRemove }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2,
      py: 1.25,
      borderRadius: "12px",
      background: "#F7F9F7",
      border: "1px solid rgba(0,0,0,0.05)",
      width: "100%",
      minWidth: 0,
    }}
  >
    <Icon icon="mdi:file-outline" color="#5B6B61" width={18} style={{ flexShrink: 0 }} />
    <OverflowTooltip title={name}>
      <Typography
        sx={{
          fontSize: 13,
          fontFamily: "Poppins, sans-serif",
          color: "#0B1F15",
          fontWeight: 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Typography>
    </OverflowTooltip>
    {size !== undefined && (
      <Typography
        sx={{
          fontSize: 12,
          color: "#8A9B90",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 300,
          flexShrink: 0,
          mr: 0.5,
        }}
      >
        {formatSize(size)}
      </Typography>
    )}
    <Box
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: "8px",
        background: "rgba(91,107,97,0.08)",
        fontSize: 11,
        color: "#5B6B61",
        fontFamily: "Poppins, sans-serif",
        fontWeight: 500,
        flexShrink: 0,
        lineHeight: "18px",
      }}
    >
      queued
    </Box>
    {onRemove && <RemoveButton onRemove={onRemove} />}
  </Box>
);

const UploadResultsPanel: React.FC<UploadResultsPanelProps> = ({
  controller,
  pendingFiles,
  onRemoveFile,
  onResubmit,
  getPreviewUrl,
}) => {
  const { session, items, progressByItemId, retryItem, submittedOriginalNames } = controller;
  const [activeFilter, setActiveFilter] = useState<UploadFilter>("ALL");
  const [expandedItemIds, setExpandedItemIds] = useState<Set<string>>(new Set());
  const [existingAccordionExpanded, setExistingAccordionExpanded] = useState(true);
  const prevStatusRef = useRef<string | null>(null);
  const startedAtRef = useRef<number | null>(null);

  const preUploadExisting = useMemo(
    () => (pendingFiles ?? []).filter((f): f is CaseFile => !("file" in f)),
    [pendingFiles]
  );
  const preUploadNew = useMemo(
    () => (pendingFiles ?? []).filter((f): f is FileUpload => "file" in f && f.file instanceof File),
    [pendingFiles]
  );

  useEffect(() => {
    setExistingAccordionExpanded(preUploadNew.length === 0);
  }, [preUploadNew.length]);

  // Track when session starts
  useEffect(() => {
    if (session && !startedAtRef.current) {
      startedAtRef.current = Date.now();
    }
    if (!session) {
      startedAtRef.current = null;
    }
  }, [session]);

  // Auto-flip filter to ERRORS on session FAILED (once)
  useEffect(() => {
    if (!session) return;
    const prev = prevStatusRef.current;
    const current = session.status;
    if (prev !== "FAILED" && current === "FAILED") {
      setActiveFilter("ERRORS");
    }
    prevStatusRef.current = current;
  }, [session?.status]);

  // Auto-expand zip parents when filter would hide their rejected children
  useEffect(() => {
    if (activeFilter === "ERRORS") {
      const parentIds = new Set<string>();
      for (const item of items) {
        if (item.parent_item_id && item.status === "REJECTED") {
          parentIds.add(item.parent_item_id);
        }
      }
      if (parentIds.size > 0) {
        setExpandedItemIds((prev) => {
          const next = new Set(prev);
          for (const id of parentIds) next.add(id);
          return next;
        });
      }
    }
  }, [activeFilter, items]);

  const toggleExpand = useCallback((itemId: string) => {
    setExpandedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  // Build a tree: top-level items and their children
  const { topLevelItems, childrenByParentId } = useMemo(() => {
    const childrenByParentId: Record<string, UploadItem[]> = {};
    const topLevel: UploadItem[] = [];
    for (const item of items) {
      if (item.parent_item_id) {
        if (!childrenByParentId[item.parent_item_id]) {
          childrenByParentId[item.parent_item_id] = [];
        }
        childrenByParentId[item.parent_item_id].push(item);
      } else {
        topLevel.push(item);
      }
    }
    return { topLevelItems: topLevel, childrenByParentId };
  }, [items]);

  const filteredTopLevel = useMemo(
    () => filterItems(topLevelItems, activeFilter),
    [topLevelItems, activeFilter]
  );

  // New FileUpload entries added after the session started that haven't been submitted yet.
  // Two checks are needed because the server may sanitize filenames differently than the client:
  //   1. Normalize both sides (handles space→underscore via werkzeug)
  //   2. Check the original name against submittedOriginalNames (handles cases like '+' being
  //      stripped by werkzeug rather than replaced, so "270+278.pdf" → "270278.pdf" ≠ "270_278.pdf")
  const newPendingFiles = useMemo(() => {
    if (!pendingFiles || pendingFiles.length === 0) return [];
    const sessionFilenames = new Set(items.map((i) => normalizeFilename(i.filename)));
    return pendingFiles.filter((f): f is FileUpload => {
      if (!("file" in f)) return false; // CaseFiles are passed as existing_file_ids — backend handles them
      const name = f.file.name;
      return !sessionFilenames.has(normalizeFilename(name)) && !submittedOriginalNames.has(name);
    });
  }, [pendingFiles, items, submittedOriginalNames]);

  // Switch to "ALL" when new pending files appear so they're visible
  useEffect(() => {
    if (newPendingFiles.length > 0) {
      setActiveFilter("ALL");
    }
  }, [newPendingFiles.length]);

  const counts = useMemo(() => {
    const allItems = items.filter((i) => !i.parent_item_id);
    return {
      accepted: allItems.filter((i) => i.status === "ACCEPTED").length,
      errors: allItems.filter((i) => i.status === "REJECTED").length,
      pending: allItems.filter(
        (i) => i.status === "REQUESTED" || i.status === "UPLOADED" || i.status === "PROCESSING"
      ).length,
      total: allItems.length,
    };
  }, [items]);

  const isFailed = session?.status === "FAILED";
  const canResubmit = isFailed;

  // Pre-upload state — files selected but Analyze not yet clicked.
  // Also handles the case where all session items were removed (e.g. after a failed upload)
  // and the user selects a new file — the new pending files should be visible.
  if (items.length === 0 && pendingFiles && pendingFiles.length > 0) {
    return (
      <Box sx={{ px: 0.5, py: 1, width: "100%" }}>
        {preUploadNew.length > 0 && (
          <Box sx={{ mb: preUploadExisting.length > 0 ? 2 : 0 }}>
            {preUploadExisting.length > 0 && (
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#5B6B61", fontFamily: "Poppins, sans-serif", textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>
                New uploads
              </Typography>
            )}
            <Stack spacing={0.75}>
              {preUploadNew.map((f) => (
                <PendingRow
                  key={f.file.name}
                  name={f.file.name}
                  size={f.file.size}
                  onRemove={onRemoveFile ? () => onRemoveFile(f.file.name) : undefined}
                />
              ))}
            </Stack>
          </Box>
        )}

        {preUploadExisting.length > 0 && (
          <Accordion
            expanded={existingAccordionExpanded}
            onChange={(_, expanded) => setExistingAccordionExpanded(expanded)}
            disableGutters
            elevation={0}
            sx={{
              background: "transparent",
              border: "1px solid rgba(38,106,74,0.15)",
              borderRadius: "12px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: "#266A4A", fontSize: 18 }} />}
              sx={{ minHeight: 40, px: 1.5, "& .MuiAccordionSummary-content": { my: 0.75 } }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#266A4A", fontFamily: "Poppins, sans-serif", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Previous revision
                </Typography>
                <Box sx={{ px: 1, py: 0.25, borderRadius: "8px", background: "rgba(38,106,74,0.08)", fontSize: 11, color: "#266A4A", fontFamily: "Poppins, sans-serif", fontWeight: 500, lineHeight: "18px" }}>
                  {preUploadExisting.length}
                </Box>
                <Typography sx={{ fontSize: 11, fontWeight: 300, color: "#5B6B61", fontFamily: "Poppins, sans-serif" }}>
                  · copied to next revision unless removed
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
              <Stack spacing={0.75}>
                {preUploadExisting.map((f) => (
                  <ExistingFileRow
                    key={`existing-${f.id}`}
                    name={f.filename}
                    onPreview={
                      getPreviewUrl
                        ? () => void openFileInNewTab(() => getPreviewUrl(f))
                        : undefined
                    }
                    onRemove={onRemoveFile ? () => onRemoveFile(f.filename) : undefined}
                  />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    );
  }

  if (!session && items.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "56px 32px",
          textAlign: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "rgba(38,106,74,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <Icon icon="mdi:file-tray-stack-outline" color="#266A4A" width={28} />
        </Box>
        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "16px",
            lineHeight: "22px",
            fontWeight: 500,
            color: "#0B1F15",
          }}
        >
          {dropZoneCopy.title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            lineHeight: "18px",
            fontWeight: 300,
            color: "#5B6B61",
          }}
        >
          {dropZoneCopy.subtitle}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: "860px",
        width: "100%",
        borderRadius: "24px",
        background: "#FAFCF8",
        boxShadow:
          "0px 1px 0px rgba(0,0,0,0.04), 0px 24px 60px -32px rgba(38,106,74,0.18)",
        padding: "28px 24px 20px",
        margin: "auto",
      }}
    >
      {/* Hook-level error (e.g. finish failed, start failed) */}
      {controller.error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: "12px" }}
          onClose={() => controller.reset()}
        >
          {controller.error}
        </Alert>
      )}

      {/* Session failed banner */}
      {isFailed && session && (
        <SessionFailedBanner
          session={session}
          canResubmit={canResubmit}
          onResubmit={onResubmit ?? (() => {})}
          counts={counts}
        />
      )}

      {/* Status strip (sticky) */}
      <Box
        sx={{
          overflow: "hidden",
          borderRadius: "12px 12px 0 0",
          mx: -3,
          px: 3,
        }}
      >
        <StatusStrip
          counts={counts}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          sessionStartedAt={startedAtRef.current}
          sessionStatus={session?.status ?? null}
          hasError={!!controller.error}
        />
      </Box>

      {/* Item ledger (scrollable) */}
      <Box
        sx={{
          overflowY: "auto",
          maxHeight: "calc(100vh - 320px)",
          overscrollBehavior: "contain",
          mt: 1,
        }}
      >
        {filteredTopLevel.length === 0 && activeFilter !== "ALL" ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#5B6B61" }}>
              {emptyFilter}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {filteredTopLevel.map((item) => {
              const children = childrenByParentId[item.item_id] ?? [];
              const isExpanded = expandedItemIds.has(item.item_id);

              return (
                <Box key={item.item_id}>
                  <UploadItemRow
                    item={item}
                    depth={0}
                    bytesUploaded={progressByItemId[item.item_id] ?? 0}
                    expanded={isExpanded}
                    onToggleExpand={() => toggleExpand(item.item_id)}
                    onRetry={() => retryItem(item.item_id)}
                    onRemove={
                      (controller.error || isFailed) && onRemoveFile
                        ? () => {
                            controller.removeItem(item.item_id);
                            onRemoveFile(item.filename);
                          }
                        : undefined
                    }
                  >
                    {children.length > 0 && (
                      <Stack spacing={0.5} sx={{ ml: "4px" }}>
                        {children.map((child) => (
                          <UploadItemRow
                            key={child.item_id}
                            item={child}
                            depth={1}
                            bytesUploaded={progressByItemId[child.item_id] ?? 0}
                            expanded={expandedItemIds.has(child.item_id)}
                            onToggleExpand={() => toggleExpand(child.item_id)}
                            onRetry={() => retryItem(child.item_id)}
                          >
                            {null}
                          </UploadItemRow>
                        ))}
                      </Stack>
                    )}
                  </UploadItemRow>
                </Box>
              );
            })}
            {activeFilter === "ALL" && newPendingFiles.map((f) => (
              <PendingRow
                key={`new-pending-${f.file.name}`}
                name={f.file.name}
                size={f.file.size}
                onRemove={onRemoveFile ? () => onRemoveFile(f.file.name) : undefined}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default UploadResultsPanel;
